import { Button as AntButton } from 'antd';
import {
  ButtonProps as AntButtonProps,
  ButtonType as AntButtonType,
} from 'antd/es/button';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { ifNotProp, ifProp, prop, switchProp } from 'styled-tools';

type IconPlacementType = 'left' | 'right';

declare const _CustomButtonTypes: ['tertiary', 'success', 'secondary'];
type CustomButtonType = (typeof _CustomButtonTypes)[number];

type ButtonType = AntButtonType | CustomButtonType;

export interface ButtonProps extends Omit<AntButtonProps, 'type'> {
  /** Determines the placement of the icon relative to the button text. Defaults to 'left' */
  iconPlacement?: IconPlacementType;

  /**
   * Button type that determines the visual style.
   * Can be one of: 'default' | 'primary' | 'ghost' | 'dashed' | 'link' | 'text' | 'tertiary' | 'success' | 'secondary'
   */
  type?: ButtonType;

  /**
   * When true, disables the hover effect on the button.
   * Primarily used for text and link type buttons.
   */
  hideHoverEffect?: boolean;

  /**
   * When true, removes the border radius on hover.
   * Primarily used for text and link type buttons.
   */
  hideHoverBorderRadius?: boolean;
}

const defaultIconPlacement = 'left';
const DefaultHorizontalPadding = css`
  padding-left: var(--spacing-05);
  padding-right: var(--spacing-05);
  ${ifProp(
    'icon',
    switchProp(prop<IconPlacementType>('iconPlacement', defaultIconPlacement), {
      right: css`
        & > svg {
          margin-right: 0;
          margin-left: var(--spacing-02);
        }
      `,
    }),
  )};
`;

const IconOnlyButton = css`
  ${ifProp(
    'icon',
    ifNotProp(
      'children',
      css`
        justify-content: center;
        &[disabled] > svg {
          fill-opacity: 0.4;
        }
        & > svg {
          margin: 0;
        }
        padding: 0;
        ${switchProp(prop<SizeType>('size', 'middle'), {
          middle: css`
            width: var(--height-base);
            min-width: var(--height-base);
            height: var(--height-base);
          `,
          small: css`
            width: var(--height-sm);
            min-width: var(--height-sm);
            height: var(--height-sm);
          `,
          large: css`
            width: var(--height-lg);
            min-width: var(--height-lg);
            height: var(--height-lg);
          `,
        })};
      `,
    ),
  )};
`;

export const PrimaryButtonBackgroundStyle = css`
  text-shadow: none;
  &[disabled] {
    background-color: var(--button-disabled);
    border-color: var(--button-disabled);
    color: var(--text-on-color-disabled);
  }
  &:not([disabled]) {
    background-color: var(--button-primary);
    border-color: var(--button-primary);
    color: var(--text-on-color);
  }
  &:not([disabled]):hover {
    opacity: 0.8;
  }
  &:not([disabled]):active {
    opacity: 0.8;
    border-color: var(--orange-10);
  }
  &:not([disabled]):focus {
    border-color: var(--orange-10);
  }
`;

export const TertiaryButtonStyle = css`
  &[disabled] {
    background-color: var(--button-disabled);
    border-color: var(--button-disabled);
    color: var(--text-on-color-disabled);
  }
  &:not([disabled]) {
    color: var(--text-primary);
    background-color: var(--button-secondary);
    border-color: var(--button-secondary);
  }
  &:not([disabled]):hover {
    background-color: var(--button-secondary-hover);
  }
  &:not([disabled]):active {
    background-color: var(--button-secondary-hover);
    border-color: var(--scarlet-gray-100);
  }
  &:not([disabled]):focus {
    background-color: var(--button-secondary);
    border-color: var(--scarlet-gray-100);
  }
`;
export const TextButtonStyle = css`
  &[disabled] {
    background-color: transparent;
    border-color: transparent;
    color: var(--text-on-color-disabled);
  }
  &:not([disabled]) {
    color: var(--text-primary);
    background-color: transparent;
    border-color: transparent;
  }
  &:not([disabled]):hover {
    background-color: var(--button-secondary-hover);
  }
  &:not([disabled]):active {
    background-color: var(--button-secondary-hover);
    border-color: var(--scarlet-gray-100);
  }
  &:not([disabled]):focus {
    background-color: var(--button-secondary);
    border-color: var(--scarlet-gray-100);
  }
`;

export const SuccessButtonStyle = css`
  &[disabled] {
    background-color: var(--button-disabled);
    border-color: var(--button-disabled);
    color: var(--text-on-color-disabled);
  }
  &:not([disabled]) {
    color: var(--text-on-color);
    background-color: var(--green-50);
    border-color: var(--green-50);
  }
  &:not([disabled]):hover {
    background-color: var(--green-60);
    border-color: var(--green-60);
  }
  &:not([disabled]):active {
    background-color: var(--green-60);
    border-color: var(--green-50);
  }
  &:not([disabled]):focus {
    background-color: var(--green-60);
    border-color: var(--green-50);
  }
`;
export const DangerButtonStyle = css`
  &[disabled] {
    background-color: var(--button-disabled);
    border-color: var(--button-disabled);
    color: var(--text-on-color-disabled);
  }
  &:not([disabled]) {
    color: var(--text-on-color);
    background-color: var(--red-60);
    border-color: var(--red-60);
  }
  &:not([disabled]):hover {
    background-color: var(--red-70);
    border-color: var(--red-70);
  }
  &:not([disabled]):active {
    background-color: var(--red-70);
    border-color: var(--red-60);
  }
  &:not([disabled]):focus {
    background-color: var(--red-70);
    border-color: var(--red-60);
  }
`;
export const ButtonStyle = css`
  display: inline-flex;
  align-items: center;
  border-radius: var(--border-radius-small);
  padding: 0 var(--spacing-03);
`;

const ButtonWithRef = forwardRef<any, ButtonProps>((props, ref) => (
  <AntButton ref={ref} {...props} type={props.type as AntButtonType} />
));

ButtonWithRef.displayName = 'ButtonWithRef';

export const Button = styled(ButtonWithRef)<ButtonProps>`
  ${ButtonStyle};
  box-shadow: none;
  ${ifProp(
    'icon',
    css`
      ${switchProp(
        prop<IconPlacementType>('iconPlacement', defaultIconPlacement),
        {
          left: css`
            flex-direction: row;
            & > svg {
              margin-right: var(--spacing-03);
            }
          `,
          right: css`
            flex-direction: row-reverse;
            & .ant-btn-loading-icon,
            & > svg {
              margin-right: 0;
              margin-left: var(--spacing-03);
            }
          `,
        },
      )};
    `,
  )};
  ${switchProp(prop<SizeType>('size', 'middle'), {
    middle: css`
      height: var(--height-base);
    `,
    small: css`
      height: var(--height-sm);
    `,
    large: css`
      height: var(--height-lg);
    `,
  })};

  .ant-btn-loading-icon {
    margin-right: 4px;
  }

  ${switchProp(prop('type'), {
    primary: ifNotProp(
      'danger',
      css`
        ${PrimaryButtonBackgroundStyle};
      `,
    ),
  })};

  ${switchProp(prop('type'), {
    tertiary: ifNotProp(
      'danger',
      css`
        ${TertiaryButtonStyle};
      `,
    ),
  })};
  ${switchProp(prop('type', 'default'), {
    default: ifNotProp(
      'danger',
      css`
        ${TertiaryButtonStyle};
      `,
    ),
  })};

  ${switchProp(prop('type'), {
    secondary: ifNotProp(
      'danger',
      css`
        ${TertiaryButtonStyle};
      `,
    ),
  })};

  ${switchProp(prop('type'), {
    success: ifNotProp(
      'danger',
      css`
        ${SuccessButtonStyle};
      `,
    ),
  })};

  ${switchProp(prop('type'), {
    text: ifNotProp(
      'danger',
      css`
        ${TextButtonStyle};
      `,
    ),
  })};

  ${switchProp(prop<ButtonType>('type', 'default'), {
    text: css`
      ${DefaultHorizontalPadding}
      &:hover, &:active, &:focus {
        /* to match slight grey color hover which appears on other buttons */
        ${ifProp(
          'hideHoverEffect',
          css`
            background-color: transparent;
          `,
          css`
            /* background-color: var(--black-with-opacity); */
            background-color: var(--scarlet-gray-50);
          `,
        )};
        ${ifProp(
          'hideHoverBorderRadius',
          css`
            border-radius: 0;
          `,
          css`
            border-radius: var(--border-radius-small);
          `,
        )};
      }
      &:disabled:disabled {
        background-color: transparent;
      }
    `,
    link: css`
      ${DefaultHorizontalPadding}
      &:hover,
      &:active,
      &:focus {
        ${ifNotProp(
          'disabled',
          css`
            color: var(--blue-70);
          `,
        )};
        ${ifProp(
          'danger',
          css`
            color: var(--red-60);
          `,
        )};
        /* to match slight grey color hover which appears on other buttons */
        ${ifProp(
          'hideHoverEffect',
          css`
            text-decoration: none;
          `,
          css`
            text-decoration: underline;
          `,
        )};
        ${ifProp(
          'hideHoverBorderRadius',
          css`
            border-radius: 0;
          `,
          css`
            border-radius: var(--border-radius-small);
          `,
        )};
        ${ifProp(
          'disabled',
          css`
            text-decoration: none;
          `,
        )};
      }
      &:disabled:disabled {
        background-color: transparent;
      }
    `,
  })};

  ${IconOnlyButton};
`;
